import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

const T = {
  navy: "#A81026",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
};

const Q = {
  arena:     "#1f8f55",
  blind:     "#6d28d9",
  concealed: "#1d4ed8",
  discover:  "#d88a1f",
};
const NEUTRAL = "#9AA5B1";

// ── Profile → color map (from GlobalJHResponses) ──────────────────────────────
const PROFILE_COLOR = {
  arena:   { bg: "#cdf5dc", color: "#1f8f55", border: "#bbf7d0" },
  driver:  { bg: "#e6e0ff", color: "#6d28d9", border: "#ddd6fe" },
  advisor: { bg: "#cfe4ff", color: "#1d4ed8", border: "#bfdbfe" },
  guarded: { bg: "#fdecb0", color: "#d88a1f", border: "#fde68a" },
};

// ── Static lookup tables (mirrored from JHResult.jsx) ────────────────────────

const PROFILE_DEFINITIONS = [
  {
    key: "arena",
    label: "Arena",
    tagline: '"Open & trusted communicator"',
    thresholds: "High Exposure · High Feedback",
    overview:
      "You display high levels of transparency and receptivity. You are a natural trust-builder who fosters psychological safety within your team. Your openness to both sharing and receiving creates an environment where honest dialogue flourishes.",
    strategy:
      "Focus on mentoring team members to model your communication style. Help others feel safe enough to expand their own Open Arena.",
    strengths:
      "Builds psychological safety, accelerates trust, reduces misunderstanding, and enables high-performing collaboration.",
    watchOut:
      "Over-disclosure or unsolicited feedback can sometimes feel intrusive — calibrate openness to the relationship and context.",
  },
  {
    key: "driver",
    label: "Blind",
    tagline: '"Visionary but occasionally unilateral"',
    thresholds: "High Exposure · Low/Avg Feedback",
    overview:
      "You are proactive in sharing your vision and perspective, but may be inadvertently minimizing team input. You tend to lead from the front with confidence, which can be inspiring, but the loop back from others is underutilised.",
    strategy:
      "Adopt a 'Feedback-First' protocol. Always solicit three distinct perspectives before stating your final position in key discussions.",
    strengths:
      "Clear communicator, decisive, confident in sharing views, sets direction for others.",
    watchOut:
      "Can create one-directional conversations; team members may feel unheard or hesitant to offer candid pushback.",
  },
  {
    key: "advisor",
    label: "Concealed",
    tagline: '"Excellent listener, careful discloser"',
    thresholds: "Low/Avg Exposure · High Feedback",
    overview:
      "You excel at listening and drawing out the perspectives of others, but may be holding back your own expertise or reasoning. Your team benefits from your attentiveness but may lack visibility into your rationale.",
    strategy:
      "Practice 'Intentional Disclosure'—your team needs to understand your thinking to fully align with your goals. Share the 'why' behind your decisions more often.",
    strengths:
      "Empathetic listener, skilled at gathering information, makes others feel heard and valued.",
    watchOut:
      "Withholding your perspective can create ambiguity about your position and reduce your perceived decisiveness.",
  },
  {
    key: "guarded",
    label: "Discover",
    tagline: '"Private and self-contained"',
    thresholds: "Low Exposure · Low Feedback",
    overview:
      "You maintain a professional and reserved style. This provides privacy and composure but may limit deep team collaboration and reduce the trust signals that enable high-performing teams.",
    strategy:
      "Small, consistent, low-stakes disclosures will build the rapport necessary to solicit more meaningful feedback over time. Start with sharing context on decisions you have already made.",
    strengths:
      "Composed, careful, considered — rarely caught off-guard; brings stability to uncertain situations.",
    watchOut:
      "Low transparency can create speculation or mistrust; others may struggle to read your intentions or feel excluded from your thinking.",
  },
];

const DIMENSION_DEFINITIONS = [
  {
    key: "exposure",
    label: "Exposure (Disclosure)",
    shortLabel: "Exposure",
    desc: "Your willingness to share your own thoughts, feelings, rationale, and perspective with others. High exposure indicates that you communicate openly and proactively, reducing the size of your Concealed quadrant.",
  },
  {
    key: "feedback",
    label: "Feedback Receptivity",
    shortLabel: "Feedback",
    desc: "Your openness to receiving and actively soliciting perspectives, observations, and input from others. High feedback receptivity reduces your Blind quadrant by bringing in information you may not have about yourself.",
  },
];

function scoreBand(val) {
  if (val >= 7) return "High";
  if (val >= 4) return "Average";
  return "Low";
}

function deriveProfile(exposure, feedback) {
  const eBand = scoreBand(exposure);
  const fBand = scoreBand(feedback);
  if (eBand === "High" && fBand === "High") return "arena";
  if (eBand === "High") return "driver";
  if (fBand === "High") return "advisor";
  return "guarded";
}

// ── Johari Window chart for PDF (plain HTML/CSS — html2canvas renders SVG
//    <text transform="rotate(...)"> unreliably, so this avoids SVG entirely) ──
function JohariWindowSVG({ exposure, feedback, activeLabel }) {
  const cW = 320;
  const cH = 320;

  const rawExp = Number.isFinite(Number(exposure)) ? Number(exposure) : 5;
  const rawFb  = Number.isFinite(Number(feedback)) ? Number(feedback) : 5;

  const xSplit = (rawFb / 10) * cW;
  const ySplit = (rawExp / 10) * cH;

  const quads = [
    { left: 0,      top: 0,      w: xSplit,    h: ySplit,    fill: Q.arena,     alpha: .22, label: "ARENA",     sub: "Open" },
    { left: xSplit, top: 0,      w: cW-xSplit, h: ySplit,    fill: Q.blind,     alpha: .16, label: "BLIND",     sub: "Unknown to self" },
    { left: 0,      top: ySplit, w: xSplit,    h: cH-ySplit, fill: Q.concealed, alpha: .14, label: "CONCEALED", sub: "Hidden" },
    { left: xSplit, top: ySplit, w: cW-xSplit, h: cH-ySplit, fill: Q.discover,  alpha: .12, label: "DISCOVER",  sub: "Unknown" },
  ].map(q => q.label === activeLabel
    ? q
    : { ...q, fill: NEUTRAL, alpha: .14 });

  const axisLabelStyle = {
    fontSize: 9, fontWeight: 700, color: T.slate,
    fontFamily: "Arial, sans-serif", letterSpacing: ".06em",
    textTransform: "uppercase", textAlign: "center",
  };

  return (
    <div style={{ width: cW + 84, margin: "0 auto" }}>
      {/* Top axis labels */}
      <div style={{ display: "flex", marginLeft: 44 }}>
        <div style={{ ...axisLabelStyle, width: xSplit }}>Known to self</div>
        <div style={{ ...axisLabelStyle, width: cW - xSplit }}>Unknown to self</div>
      </div>

      <div style={{ display: "flex", marginTop: 12 }}>
        {/* Left axis labels (rotated, matching original vertical style) */}
        <div style={{ width: 40, marginRight: 4, position: "relative" }}>
          <div style={{
            position: "absolute", top: ySplit / 2, left: 20,
            transform: "translate(-50%, -50%) rotate(-90deg)",
            whiteSpace: "nowrap", ...axisLabelStyle, width: "auto",
          }}>Known to others</div>
          <div style={{
            position: "absolute", top: ySplit + (cH - ySplit) / 2, left: 20,
            transform: "translate(-50%, -50%) rotate(-90deg)",
            whiteSpace: "nowrap", ...axisLabelStyle, width: "auto",
          }}>Unknown to others</div>
        </div>

        {/* Chart square */}
        <div style={{ position: "relative", width: cW, height: cH, border: `1.5px solid ${T.line}`, borderRadius: 4, background: "#fff" }}>
          {quads.map((q, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: q.left, top: q.top, width: q.w, height: q.h,
                background: q.fill, opacity: q.alpha,
              }}
            />
          ))}

          {/* Dividers */}
          <div style={{ position: "absolute", left: xSplit - 1, top: 0, width: 0, height: cH, borderLeft: `1.5px dashed ${T.navy}` }} />
          <div style={{ position: "absolute", top: ySplit - 1, left: 0, height: 0, width: cW, borderTop: `1.5px dashed ${T.navy}` }} />

          {/* Score badges */}
          <div style={{
            position: "absolute", left: xSplit - 18, top: cH - 16, width: 36, height: 16,
            background: T.navy, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 9, fontWeight: 700, color: "#fff", fontFamily: "Arial, sans-serif",
          }}>F:{rawFb}</div>

          <div style={{
            position: "absolute", left: cW - 30, top: ySplit - 8, width: 32, height: 16,
            background: T.navy, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 9, fontWeight: 700, color: "#fff", fontFamily: "Arial, sans-serif",
          }}>E:{rawExp}</div>

          {/* Quadrant labels */}
          {quads.map((q, i) => {
            if (q.w < 12 || q.h < 12) return null;
            return (
              <div key={i} style={{
                position: "absolute", left: q.left, top: q.top, width: q.w, height: q.h,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                textAlign: "center", padding: 4,
              }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: q.fill, fontFamily: "Arial, sans-serif", letterSpacing: ".08em" }}>{q.label}</span>
                {q.h > 36 && (
                  <span style={{ fontSize: 9, color: T.slate, fontFamily: "Arial, sans-serif", marginTop: 4 }}>{q.sub}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Right axis label */}
        <div style={{ width: 40, marginLeft: 8, position: "relative" }}>
          <div style={{
            position: "absolute", top: cH / 2, left: 20,
            transform: "translate(-50%, -50%) rotate(90deg)",
            whiteSpace: "nowrap",
            fontSize: 9, fontWeight: 700, color: T.navy, fontFamily: "Arial, sans-serif", letterSpacing: ".05em",
          }}>
            Disclosure →
          </div>
        </div>
      </div>

      {/* Bottom axis label */}
      <div style={{ marginLeft: 44, textAlign: "center", marginTop: 14, fontSize: 9, fontWeight: 700, color: T.navy, fontFamily: "Arial, sans-serif", letterSpacing: ".05em" }}>
        Feedback →
      </div>
    </div>
  );
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function HiddenJHReport({ participantData, onComplete }) {
  const containerRef = useRef(null);
  const hasRun = useRef(false);

  // ── Load html2canvas + jsPDF once ─────────────────────────────────────────
  useEffect(() => {
    let pending = 2;
    const done = () => { pending--; };
    const load = (src, id) => {
      if (document.getElementById(id)) { done(); return; }
      const s = document.createElement("script");
      s.id = id;
      s.src = src;
      s.onload = done;
      s.onerror = done;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-jh-v1");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",         "jspdf-jh-v1");
  }, []);

  // ── Generate PDF whenever participantData is set ──────────────────────────
  useEffect(() => {
    if (!participantData) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const generate = async () => {
      // Wait for both scripts
      await new Promise((res) => {
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
          setTimeout(check, 100);
        };
        check();
      });

      // Allow fonts + SVG fills to fully paint
      await new Promise((r) => setTimeout(r, 1400));

      const el = containerRef.current;
      if (!el) { hasRun.current = false; onComplete?.(); return; }


      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".jh-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width:       pageEls[i].scrollWidth,
            height:      pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData  = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width  / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX  = 25.4 / 96;
          const naturalW   = logicalW * MM_PER_PX;
          const naturalH   = logicalH * MM_PER_PX;
          const scaleToW   = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.userInfo?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`JH_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("JH PDF generation failed", err);
      } finally {
        hasRun.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!participantData) return null;

  // ── Derive all display values ──────────────────────────────────────────────
  const {
    userInfo     = {},
    scores       = {},
    activeProfile: profileKey,
    insightText  = "",
    submission_time
  } = participantData;

  const participant    = userInfo.name || "—";
  const assessmentDate = submission_time ? formatDate(new Date(submission_time)) : "—";

  const exposure = Number(scores.exposure) || 0;
  const feedback = Number(scores.feedback) || 0;

  const resolvedProfile = profileKey ?? deriveProfile(exposure, feedback);
  const activeProfDef   = PROFILE_DEFINITIONS.find(p => p.key === resolvedProfile);

  const eBand = scoreBand(exposure);
  const fBand = scoreBand(feedback);

  const dimensionsForDisplay = DIMENSION_DEFINITIONS.map(d => ({
    def:   d,
    score: scores[d.key] ?? null,
    band:  scoreBand(scores[d.key] ?? 0),
  }));

  const BAR_FILL = `linear-gradient(90deg,${T.navy},${T.blue},${T.gold})`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      {/* Font */}
      <link
        href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap"
        rel="stylesheet"
      />

      {/* ── All CSS scoped under .jh-pdf-root ── */}
      <style>{`
        .jh-pdf-root {
          font-family: 'Satoshi', sans-serif;
          background: linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
          color: #1D2733;
          line-height: 1.5;
        }
        .jh-pdf-root .report { max-width:1100px; margin:0 auto; padding:2rem 1rem 4rem; }

        /* ── Page shell ── */
        .jh-pdf-root .jh-page {
          background:#ffffff;
          border:1px solid #DCE5EF;
          border-radius:28px;
          box-shadow:0 18px 40px rgba(14,39,68,.10);
          overflow:hidden;
          margin-bottom:2rem;
        }

        /* ── Cover ── */
        .jh-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .jh-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
        .jh-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem; position:relative; z-index:2; }
        .jh-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }

        .jh-pdf-root .logo-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: 500px;
          height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        .jh-pdf-root .logo-panel-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          padding-left: 60px;
          padding-bottom: 25px;
        }
        .jh-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        .jh-pdf-root .cover-title h2 {margin:0 0 1rem; font-size:3.4rem;line-height:.95; color:#FFFFFF;white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem;}
        .jh-pdf-root .cover-title p  { font-size:1.2rem; color:rgba(255,255,255,.82); max-width:100%; text-align:justify; }
        .jh-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
        .jh-pdf-root .meta { background:rgba(255,255,255,.1); font-size:1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
        .jh-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .jh-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .jh-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem; }
        .jh-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .jh-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:1rem; }

        /* ── Inner pages ── */
        .jh-pdf-root .page-inner { padding:2.5rem; }
        .jh-pdf-root .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:2rem; }
        .jh-pdf-root .section-kicker { font-size:0.8rem; text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
        .jh-pdf-root .section-title { margin:.35rem 0 0; font-size:clamp(1.45rem,1.2rem + 1.1vw,2rem); color:#0f2744; border-left: 5px solid #C4163A; padding-left: .75rem; }

        /* ── Cards ── */
        .jh-pdf-root .card { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid #DCE5EF; border-radius:20px; padding:1.5rem; }
        .jh-pdf-root .card h3 { margin:0 0 .75rem; font-size:1.2rem; color:#0f2744; }
        .jh-pdf-root .lead { color:#334155; font-size:1rem; margin:0; text-align:justify; }

        /* ── Inner sub-panel ── */
        .jh-pdf-root .sub-panel { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid #DCE5EF; border-radius:20px; padding:1.25rem; }
        .jh-pdf-root .sub-panel h4 { margin:0 0 .6rem; font-size:1rem; color:#0f2744; }

        /* ── Score tiles ── */
        .jh-pdf-root .score-tile-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:.8rem; margin-bottom:.85rem; }
        .jh-pdf-root .score-tile { border-radius:14px; border:1px solid #DCE5EF; padding:.85rem; background:linear-gradient(180deg,#fff,#f8fbff); }
        .jh-pdf-root .score-tile span   { display:block; font-size:0.72rem; letter-spacing:.08em; text-transform:uppercase; color:#5B677A; font-weight:700; margin-bottom:.2rem; }
        .jh-pdf-root .score-tile strong { font-size:1.85rem; color:#A81026; font-weight:700; display:block; }
        .jh-pdf-root .score-tile .band  { font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; margin-top:.2rem; }

        /* ── Bars ── */
        .jh-pdf-root .bar-row { display:grid; grid-template-columns:170px 1fr 48px; gap:10px; align-items:center; margin-bottom:.65rem; }
        .jh-pdf-root .bar-label { font-size:0.88rem; font-weight:600; color:#1D2733; }
        .jh-pdf-root .track { height:10px; background:#EAF0F6; border-radius:999px; overflow:hidden; }
        .jh-pdf-root .fill  { height:100%; border-radius:999px; }
        .jh-pdf-root .score-val { text-align:right; font-size:0.85rem; font-weight:700; color:#A81026; }

        /* ── Callout ── */
        .jh-pdf-root .callout { background:linear-gradient(135deg,#fff7f7,#fbeaea); border:1px solid #DCE5EF; border-radius:18px; padding:1.25rem 1.5rem; color:#334155; text-align:justify; font-size:0.9rem; line-height:1.6; }

        /* ── Profile card ── */
        .jh-pdf-root .profile-card { border:1px solid #DCE5EF; border-radius:16px; padding:1rem; margin-bottom:.75rem; background:#fff; border-left-width:5px; }
        .jh-pdf-root .profile-card.active { box-shadow:0 0 0 2px rgba(0,0,0,.08); }
        .jh-pdf-root .profile-header { display:flex; align-items:center; gap:.85rem; margin-bottom:.6rem; }
        .jh-pdf-root .profile-dot { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
        .jh-pdf-root .profile-meta strong { display:block; font-size:.97rem; color:#1D2733; }
        .jh-pdf-root .profile-meta em     { font-style:italic; font-size:.82rem; color:#5B677A; }
        .jh-pdf-root .profile-meta .thresholds { font-size:.75rem; color:#5B677A; margin-top:.15rem; }
        .jh-pdf-root .profile-card p { margin:0 0 .35rem; font-size:0.88rem; color:#334155; text-align:justify; }
        .jh-pdf-root .your-tag { font-size:.72rem; font-weight:700; margin-left:.5rem; }

        /* ── Two-col ── */
        .jh-pdf-root .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1.2rem; }

        /* ── Growth levers ── */
        .jh-pdf-root .lever-grid { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
        .jh-pdf-root .lever { border-radius:14px; padding:1rem; }

        /* ── Back cover ── */
        .jh-pdf-root .back-cover { min-height:1556px; color:white; }
        .jh-pdf-root .back-cover .page-inner { min-height:1556px; display:flex; flex-direction:column; justify-content:space-between; }
        .jh-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .jh-pdf-root .closing p  { text-align:justify; max-width:100%; color:rgba(255,255,255,.82); font-size:1.2rem; }
        .jh-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
        .jh-pdf-root .contact { background:rgba(193,24,24,.1); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
        .jh-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .jh-pdf-root .contact strong { font-size:0.92rem; }
        .jh-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .jh-pdf-root .light { color:rgba(255,255,255,.66); }
      `}</style>

      <div className="jh-pdf-root">
        <main className="report">

          {/* ══════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════ */}
          <section className="jh-page cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
              minHeight: "1556px",
              flexDirection: "column",
            }}>
              {/* Logo panel — SVG white wedge + Acumen logo */}
              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>

              <div className="cover-inner">
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: 'relative', zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>Johari Window Assessment</h2>
                    <p>
                      A personalised communication and self-awareness profile covering
                      Exposure and Feedback dimensions, mapped onto the four-quadrant
                      Johari Window model.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta">
                      <span>Participant</span>
                      <strong>{participant}</strong>
                    </div>
                    <div className="meta">
                      <span>Assessment Date</span>
                      <strong>{assessmentDate}</strong>
                    </div>
                    <div className="meta">
                      <span>Report Type</span>
                      <strong>Individual Communication Profile</strong>
                    </div>
                  </div>

                  <div className="cover-badges">
                    {["Executive Dashboard", "Exposure & Feedback Scores", "Profile Insights", "Growth Strategy"].map(b => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note" style={{ position: 'relative', zIndex: 2 }}>
                  <div>Based on the Johari Window framework by Joseph Luft and Harry Ingham.</div>
                  <div>Each dimension scored out of 10.</div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 2 — EXECUTIVE DASHBOARD
          ══════════════════════════════════════════ */}
          <section className="jh-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker" style={{ color: T.gold }}>Page 2</div>
                  <h2 className="section-title">Executive Dashboard</h2>
                </div>
                <div className="small">Exposure · Feedback · Profile</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".9rem", margin: "0 0 1.25rem" }}>
                This section presents your scores across the two core Johari Window dimensions and maps them onto the four-quadrant model. A larger Arena quadrant reflects a more open, trust-enabling communication profile.
              </p>

              {/* Full-width Johari chart, dimension scores stacked below */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.25rem", marginBottom: "1.25rem" }}>

                {/* Johari Window chart — full width panel; chart itself keeps its natural proportions, centered */}
                <div className="sub-panel">
                  <h4>Your Johari Window</h4>
                  <JohariWindowSVG
                    exposure={exposure}
                    feedback={feedback}
                    activeLabel={(PROFILE_DEFINITIONS.find(p => p.key === resolvedProfile)?.label || "").toUpperCase()}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4,auto)", justifyContent: "center", gap: "18px", marginTop: "10px" }}>
                    {[
                      { label: "Arena",     color: Q.arena },
                      { label: "Blind",     color: Q.blind },
                      { label: "Concealed", color: Q.concealed },
                      { label: "Discover",  color: Q.discover },
                    ].map(({ label, color }) => (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: ".78rem", color: T.slate, fontFamily: "Arial, sans-serif" }}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Score tiles + profile badge + insight — now below, full width */}
                <div className="sub-panel">
                  <h4>Your Dimension Scores</h4>
                  <p style={{ color: T.slate, fontSize: ".82rem", margin: "0 0 .75rem" }}>
                    Scores reflect self-reported responses on a 0–10 scale.
                  </p>

                  {/* Score tiles */}
                  <div className="score-tile-grid">
                    {dimensionsForDisplay.map(({ def, score, band }) => {
                      const bandColor = band === "High" ? T.navy : band === "Average" ? T.gold : T.slate;
                      return (
                        <div className="score-tile" key={def.key}>
                          <span>{def.shortLabel}</span>
                          <strong>{score ?? "—"}<span style={{ fontSize: "1rem", color: T.slate, fontWeight: 400 }}>/10</span></strong>
                          <div className="band" style={{ color: bandColor }}>{band}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Profile badge */}
                  {activeProfDef && (
                    <div style={{
                      background: `linear-gradient(135deg,${T.navy}10,${T.blue}08)`,
                      border: `1px solid ${T.navy}30`,
                      borderRadius: 12,
                      padding: "10px 12px",
                      marginBottom: ".75rem",
                    }}>
                      <div style={{ fontSize: ".72rem", fontWeight: 700, color: T.slate, textTransform: "uppercase", letterSpacing: ".07em" }}>Your Profile</div>
                      <div style={{ fontSize: "1.35rem", fontWeight: 700, color: T.navy, marginTop: 2 }}>{activeProfDef.label}</div>
                      <div style={{ fontSize: ".8rem", color: T.slate, fontStyle: "italic" }}>{activeProfDef.tagline}</div>
                    </div>
                  )}

                  {/* Insight callout */}
                  <div className="callout">
                    <strong>Evaluation insight: </strong>
                    {insightText || "Your Johari Window profile reflects your current communication strengths and areas for intentional development."}
                  </div>
                </div>
              </div>

              {/* Dimension bars + band reference */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
                <div className="card">
                  <h3>Dimension Scores at a Glance <span style={{ fontSize: ".78rem", color: T.slate, fontWeight: 400 }}>— out of 10</span></h3>
                  {dimensionsForDisplay.map(({ def, score }) => (
                    <div className="bar-row" key={def.key}>
                      <div className="bar-label">{def.shortLabel}</div>
                      <div className="track">
                        <div className="fill" style={{ width: `${((score ?? 0) / 10) * 100}%`, background: BAR_FILL }} />
                      </div>
                      <div className="score-val">{score ?? "—"}</div>
                    </div>
                  ))}
                </div>

                <div className="card">
                  <h3>Score Bands</h3>
                  {[
                    { band: "High",    range: "7–10", color: T.navy },
                    { band: "Average", range: "4–6",  color: T.gold },
                    { band: "Low",     range: "0–3",  color: T.slate },
                  ].map(({ band, range, color }) => (
                    <div key={band} style={{ display: "flex", justifyContent: "space-between", fontSize: ".9rem", marginBottom: ".5rem" }}>
                      <span style={{ fontWeight: 700, color }}>{band}</span>
                      <span style={{ color: T.slate }}>{range}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: "1rem", padding: ".75rem", background: "#f8f4eb", borderRadius: "12px", border: `1px solid ${T.gold}44` }}>
                    <div style={{ fontSize: ".75rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".3rem" }}>
                      Active Profile
                    </div>
                    <div style={{ fontSize: ".88rem", color: T.ink }}>
                      <strong style={{ color: T.navy }}>{activeProfDef?.label ?? "—"}</strong>
                      {" "}— {activeProfDef?.thresholds ?? ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 3 — PROFILE INSIGHTS
          ══════════════════════════════════════════ */}
          <section className="jh-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker" style={{ color: T.gold }}>Page 3</div>
                  <h2 className="section-title">The Four Johari Profiles</h2>
                </div>
                <div className="small">Profile mapping</div>
              </div>

              <p style={{ color: T.slate, fontSize: ".88rem", margin: "0 0 1.1rem" }}>
                Your Exposure and Feedback scores together determine which of the four communication profiles best describes your current style. Your active profile is highlighted below.
              </p>

              {[...PROFILE_DEFINITIONS]
                .sort((a, b) => (a.key === resolvedProfile ? -1 : b.key === resolvedProfile ? 1 : 0))
                .map(def => {
                const isActive = def.key === resolvedProfile;
                const pc = PROFILE_COLOR[def.key] || { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
                return (
                  <div
                    className={`profile-card${isActive ? " active" : ""}`}
                    key={def.key}
                    style={{
                      borderLeft: `5px solid ${pc.color}`,
                      borderTop: isActive ? `1.5px solid ${pc.color}` : `1px solid #DCE5EF`,
                      borderRight: isActive ? `1.5px solid ${pc.color}` : `1px solid #DCE5EF`,
                      borderBottom: isActive ? `1.5px solid ${pc.color}` : `1px solid #DCE5EF`,
                      background: isActive ? `linear-gradient(135deg,${pc.bg},#ffffff 65%)` : "#fff",
                      boxShadow: isActive ? `0 0 0 2px ${pc.color}22` : "none",
                    }}
                  >
                    <div className="profile-header">
                      <div className="profile-dot" style={{ background: pc.color }} />
                      <div className="profile-meta">
                        <strong style={{ color: isActive ? pc.color : "#1D2733" }}>
                          {def.label}
                          {isActive && <span className="your-tag" style={{ color: pc.color }}>YOUR PROFILE</span>}
                        </strong>
                        <em>{def.tagline}</em>
                        <div className="thresholds">{def.thresholds}</div>
                      </div>
                    </div>
                    <p>{def.overview}</p>
                    <p><strong>Strategy:</strong> {def.strategy}</p>
                    <p><strong>Strengths:</strong> {def.strengths}</p>
                    <p><strong>Watch out for:</strong> {def.watchOut}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 4 — PERSONALISED INSIGHT & GROWTH
          ══════════════════════════════════════════ */}
          <section className="jh-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker" style={{ color: T.gold }}>Page 4</div>
                  <h2 className="section-title">Personalised Insight &amp; Growth</h2>
                </div>
                <div className="small">Tailored to your profile</div>
              </div>

              {/* Personalised insight */}
              {activeProfDef && (
                <div className="card" style={{ marginBottom: "1.25rem" }}>
                  <h3>Your {activeProfDef.label} Profile ({activeProfDef.thresholds})</h3>
                  <div className="callout" style={{ marginBottom: ".85rem" }}>
                    <strong>{activeProfDef.label}: </strong>{activeProfDef.overview}
                    <br /><br />
                    <strong>Recommended strategy: </strong>{activeProfDef.strategy}
                  </div>
                  <div className="two-col">
                    <div style={{ background: "#f8fbff", border: `1px solid ${T.line}`, borderRadius: 12, padding: 14 }}>
                      <div style={{ fontWeight: 700, color: T.navy, marginBottom: 6, fontSize: ".9rem" }}>✓ Strengths</div>
                      <p style={{ margin: 0, fontSize: ".87rem", color: T.ink, lineHeight: 1.55 }}>{activeProfDef.strengths}</p>
                    </div>
                    <div style={{ background: "#fff9e8", border: `1px solid #F2D58A`, borderRadius: 12, padding: 14 }}>
                      <div style={{ fontWeight: 700, color: T.gold, marginBottom: 6, fontSize: ".9rem" }}>⚠ Watch out for</div>
                      <p style={{ margin: 0, fontSize: ".87rem", color: T.ink, lineHeight: 1.55 }}>{activeProfDef.watchOut}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Growth levers */}
              <div className="card" style={{ marginBottom: "1.1rem" }}>
                <h3>Expanding Your Arena</h3>
                <p className="lead" style={{ marginBottom: "1rem" }}>
                  The Johari Window is not a fixed snapshot. The two levers below directly influence the size of your Arena quadrant:
                </p>
                <div className="lever-grid">
                  <div className="lever" style={{ background: `${T.navy}08`, border: `1px solid ${T.navy}20` }}>
                    <div style={{ fontWeight: 700, color: T.navy, marginBottom: 8 }}>↗ Increase Exposure</div>
                    <p style={{ margin: 0, fontSize: ".88rem", lineHeight: 1.55 }}>
                      Share your reasoning and context more openly. Explaining the "why" behind decisions reduces your Concealed area and builds team alignment.
                    </p>
                  </div>
                  <div className="lever" style={{ background: `${T.gold}12`, border: `1px solid ${T.gold}40` }}>
                    <div style={{ fontWeight: 700, color: T.gold, marginBottom: 8 }}>↗ Increase Feedback Receptivity</div>
                    <p style={{ margin: 0, fontSize: ".88rem", lineHeight: 1.55 }}>
                      Actively solicit input before and after key decisions. Creating safe channels for honest input reduces your Blind area.
                    </p>
                  </div>
                </div>
              </div>

              {/* Two-col: understanding + coaching note */}
              <div className="two-col">
                <div className="card">
                  <h3>Understanding the Johari Window</h3>
                  <p className="lead">
                    Developed by Joseph Luft and Harry Ingham, the Johari Window maps information across two axes — what is known to the self (Disclosure/Exposure) and what is known to others (Feedback). Expanding the Arena through both dimensions is the primary developmental goal.
                  </p>
                </div>
                <div className="card">
                  <h3>Growth Disclaimer</h3>
                  <p className="lead">
                    This report reflects your self-reported perceptions within your current professional context. The boundaries of your Johari Window should shift as your career progresses and team dynamics evolve. View these results as a roadmap for intentional growth, not a fixed assessment.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════ */}
          <section className="jh-page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
              minHeight: "1556px",
            }}>
              {/* Header bar */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Building Open, Authentic Communication</h2>
                    <p>
                      The Johari Window is a living framework that expands through deliberate
                      self-disclosure and active feedback-seeking. Acumen Business Catalyst
                      uses assessments like this to turn communication data into focused
                      development conversations that drive lasting behavioural change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Why Communication Transparency Matters</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
                      Research on high-performing teams consistently shows that leaders who actively expand their Arena — through increased self-disclosure and deliberate feedback-seeking — create stronger psychological safety, faster trust cycles, and more productive team dynamics than those who remain in guarded or one-directional patterns.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact">
                      <span>Brand</span>
                      <strong>Acumen Business Catalyst</strong>
                    </div>
                    <div className="contact">
                      <span>Focus</span>
                      <strong>Leadership • Culture • Capability</strong>
                    </div>
                    <div className="contact">
                      <span>Report type</span>
                      <strong>Individual Communication Profile</strong>
                    </div>
                  </div>
                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Johari Window (JH) assessment framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}